import { useEffect, useRef, useState } from 'react';
import {
  ASSET_DRAG_TYPE,
  buildPlannedQueue,
  clearFeedOrder,
  latestVersion,
  publicImageUrl,
  setFeedOrder,
} from '../../lib/marketing';
import { CalendarIcon, InstagramIcon, MoreIcon, PlusCircleIcon } from '../icons';
import logo from '../../assets/logo.png';

// Rows of the real feed to show beneath the planned queue: enough to judge
// how a new post reshapes the visible grid, without pulling the whole history.
const LIVE_ROWS = 3;

// Planned posts stacked on top of the real feed, in one grid.
//
// Instagram drops a new post top-left and pushes everything down, so a planned
// post can only be judged against the grid it will actually join. That's why
// the queue and the live feed share a grid rather than sitting in two tabs.
export default function PlannerGrid({
  assets,
  liveMedia,
  instagram,
  onReordered,
  onConnect,
  onDisconnect,
  onRefresh,
  onViewCalendar,
}) {
  const [planned, setPlanned] = useState(() => buildPlannedQueue(assets));
  const [menuOpen, setMenuOpen] = useState(false);
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    setPlanned(buildPlannedQueue(assets));
  }, [assets]);

  const live = (liveMedia || []).slice(0, LIVE_ROWS * 3);
  const hasManualOrder = planned.some((a) => a.feed_position != null);

  const persist = async (next) => {
    setPlanned(next);
    await setFeedOrder(next.map((a) => a.id));
    onReordered && (await onReordered());
  };

  const moveWithin = (from, to) => {
    const next = [...planned];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const insertFromLibrary = (assetId, dropIndex) => {
    const existing = planned.findIndex((a) => a.id === assetId);
    if (existing !== -1) return moveWithin(existing, dropIndex);
    const asset = assets.find((a) => a.id === assetId);
    if (!asset) return null;
    const next = [...planned];
    next.splice(dropIndex, 0, asset);
    return next;
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOverIndex(null);

    const incomingId = e.dataTransfer.getData(ASSET_DRAG_TYPE);
    const next = incomingId
      ? insertFromLibrary(incomingId, dropIndex)
      : from === null || from === dropIndex
        ? null
        : moveWithin(from, dropIndex);

    if (next) await persist(next);
  };

  const handleRemove = async (assetId) => {
    const next = planned.filter((a) => a.id !== assetId);
    setPlanned(next);
    await clearFeedOrder([assetId]);
    await setFeedOrder(next.map((a) => a.id));
    onReordered && (await onReordered());
  };

  const dropProps = (index) => ({
    // dragEnter as well as dragOver: some browsers won't treat the element as
    // a valid drop target — and won't fire `drop` — without both.
    onDragEnter: (e) => {
      e.preventDefault();
      setDragOverIndex(index);
    },
    onDragOver: (e) => {
      e.preventDefault();
      setDragOverIndex(index);
    },
    onDragLeave: () => setDragOverIndex((i) => (i === index ? null : i)),
    onDrop: (e) => handleDrop(e, index),
  });

  // Always leave at least one open slot, and keep the row filled out, so
  // there's somewhere obvious to drop.
  const emptySlots = Math.max(1, 3 - (planned.length % 3 || 0));

  return (
    <section className="panel planner-panel">
      <header className="planner-head">
        <img src={logo} alt="" className="planner-avatar" />
        <div className="planner-identity">
          <span className="planner-handle">
            {instagram?.username ? `@${instagram.username}` : 'Skin Fairy JA'}
          </span>
          <span className="planner-meta">
            {instagram?.connected ? `${liveMedia?.length ?? 0} live posts` : 'Not connected'}
            <span className="planner-dot">·</span>
            {planned.length} planned
          </span>
        </div>

        {instagram?.connected ? (
          <div className="planner-head-actions">
            <span className="planner-connected">
              <span className="planner-connected-dot" />
              Connected
            </span>
            <div className="planner-menu-wrap">
              <button
                className="planner-icon-button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Instagram options"
                aria-expanded={menuOpen}
              >
                <MoreIcon />
              </button>
              {menuOpen && (
                <>
                  <div className="planner-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="planner-menu" role="menu">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onRefresh();
                      }}
                    >
                      Refresh feed
                    </button>
                    {hasManualOrder && (
                      <button
                        onClick={async () => {
                          setMenuOpen(false);
                          await clearFeedOrder(
                            planned.filter((a) => a.feed_position != null).map((a) => a.id)
                          );
                          onReordered && (await onReordered());
                        }}
                      >
                        Reset planned order
                      </button>
                    )}
                    <button
                      className="planner-menu-danger"
                      onClick={() => {
                        setMenuOpen(false);
                        onDisconnect();
                      }}
                    >
                      Disconnect Instagram
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <button className="planner-connect" onClick={onConnect}>
            Connect Instagram
          </button>
        )}
      </header>

      <p className="planner-lede">Drag approved content into the grid to preview your feed.</p>

      <div className="planner-section-label">
        Planned <span>(drag to reorder)</span>
      </div>

      <div className="planner-planned-grid">
        {planned.map((asset, index) => {
          const version = latestVersion(asset);
          const isDraft = asset.status !== 'Approved';
          return (
            <div
              className={
                'planned-tile' +
                (dragOverIndex === index ? ' planned-tile-over' : '') +
                (isDraft ? ' planned-tile-draft' : '')
              }
              key={asset.id}
              draggable
              onDragStart={() => {
                dragIndex.current = index;
              }}
              {...dropProps(index)}
            >
              <img src={publicImageUrl(version.image_path)} alt={version.caption || ''} />
              <span className="planned-order">{index + 1}</span>
              {isDraft && <span className="planned-draft-tag">{asset.status}</span>}
              <button
                className="planned-remove"
                onClick={() => handleRemove(asset.id)}
                title="Remove from plan"
              >
                ×
              </button>
            </div>
          );
        })}

        {Array.from({ length: emptySlots }, (_, i) => (
          <div
            className={
              'planned-slot' + (dragOverIndex === planned.length + i ? ' planned-tile-over' : '')
            }
            key={`slot-${i}`}
            {...dropProps(planned.length + i)}
          >
            <PlusCircleIcon />
          </div>
        ))}
      </div>

      {instagram?.connected && live.length > 0 && (
        <>
          <div className="planner-section-label planner-live-label">
            <InstagramIcon />
            Live on Instagram
          </div>
          <div className="planner-live-grid">
            {live.map((post) => (
              <a
                className="planner-live-tile"
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                title={post.caption || ''}
              >
                <img src={post.imageUrl} alt={post.caption || 'Instagram post'} />
                {post.mediaType === 'CAROUSEL_ALBUM' && <span className="planner-live-badge">❏</span>}
                {post.mediaType === 'VIDEO' && <span className="planner-live-badge">▶</span>}
              </a>
            ))}
          </div>
        </>
      )}

      {!instagram?.connected && (
        <p className="planner-empty-note">
          Connect Instagram to see your real posts underneath, so you can preview how planned content
          sits against the existing grid.
        </p>
      )}

      <footer className="planner-tip">
        <span className="planner-tip-icon">
          <InstagramIcon />
        </span>
        <p>Drag approved assets from the library into the planned section above to schedule posts.</p>
        <button className="planner-tip-button" onClick={onViewCalendar}>
          <CalendarIcon />
          View Calendar
        </button>
      </footer>
    </section>
  );
}
